// Consent forms: the store's PDF library, the customer's read-to-the-end viewer, and the
// evidence staff can download. Deep-merged into the main bundle in i18n.js.

export const consentFormTranslationsEn = {
  servicesManagement: {
    tabs: {
      consentForms: 'Consent Forms',
    },
  },
  consentForms: {
    unnamed: 'Consent form',
    viewer: {
      title: 'Consent form',
      counter: '{{current}}/{{total}}',
      next: 'Next',
      acceptOne: 'Accept Consent Form',
      acceptMany: 'Accept Consent Forms',
      scrollHint: 'Scroll to the end of the document to continue.',
      readContinue: 'Read to the end. Continue to the next form.',
      acceptStatement_one: 'By accepting, you confirm you have read and agree to this consent form.',
      acceptStatement_other: 'By accepting, you confirm you have read and agree to all {{count}} consent forms.',
      download: 'Download PDF',
      loadError: 'The consent form could not be loaded.',
      endOfDocument: 'End of document',
    },
    errors: {
      accept: 'Your acceptance could not be saved. Please try again.',
      load: 'Consent forms could not be checked. Please try again.',
      required: 'Please read and accept the consent forms before continuing.',
      loading: 'Consent forms could not be loaded.',
      create: 'The consent form could not be created.',
      update: 'The consent form could not be updated.',
      upload: 'The PDF could not be uploaded.',
      archive: 'The consent form could not be archived.',
      restore: 'The consent form could not be restored.',
      preview: 'The PDF could not be opened.',
      sendRequest: 'The consent link could not be sent.',
      certificate: 'The certificate could not be downloaded.',
      acceptances: 'Accepted consent forms could not be loaded.',
      status: 'Consent status could not be loaded.',
    },
    apiErrors: {
      ConsentFormNotFound: 'This consent form no longer exists.',
      InvalidConsentFormData: 'Check the consent form details and try again.',
      ConsentFormArchived: 'This consent form is archived.',
      ConsentFormRequired: 'Please read and accept the consent forms before continuing.',
      ConsentFormVersionNotCurrent: 'The consent form was updated while you were reading it. Close this window and try again to read the latest version.',
      ConsentFormHasNoVersion: 'Upload a PDF to this consent form first.',
      ConsentFormNotReadToEnd: 'Scroll to the end of every consent form before accepting.',
      InvalidFileType: 'Only PDF files can be used as consent forms.',
      FileTooLarge: 'The PDF must be 10 MB or smaller.',
      PlanUpgradeRequired: 'Upgrade to Pro to use consent forms.',
    },
    management: {
      addNew: 'Add Consent Form',
      noForms: 'No consent forms yet',
      emptyHint: 'Upload a PDF your customers must read and accept before their first visit, then choose it on each service.',
      intro: 'Customers must scroll to the end of the PDF and accept it before booking a service that uses it, or before buying a package or membership that includes one.',
      card: {
        currentVersion: 'Version {{version}}',
        uploadedAt: 'Uploaded {{date}}',
        noFile: 'No PDF uploaded yet',
        services_one: 'Used by {{count}} service',
        services_other: 'Used by {{count}} services',
        noServices: 'Not used by any service yet',
        validity: 'Valid for {{count}} months',
        noExpiry: 'Never expires',
        active: 'Active',
        archived: 'Archived',
        fileHash: 'SHA-256',
      },
      actions: {
        edit: 'Edit',
        upload: 'Upload new version',
        uploadFirst: 'Upload PDF',
        preview: 'Preview',
        archive: 'Archive',
        restore: 'Restore',
        history: 'Version history',
      },
      dialog: {
        createTitle: 'New consent form',
        editTitle: 'Edit consent form',
        name: 'Name',
        validityMonths: 'Validity (months)',
        validityPlaceholder: 'Never expires',
        validityHelp: 'Leave empty if one acceptance is enough. Set a number of months to ask again periodically.',
        file: 'PDF file',
        save: 'Save',
        create: 'Create',
      },
      upload: {
        title: 'Upload new version',
        choose: 'Choose PDF',
        replace: 'Choose another PDF',
        fileHelp: 'PDF only, up to 10 MB. Earlier versions are kept as evidence.',
        requiresReacceptance: 'Ask customers who accepted an earlier version to accept again',
        requiresReacceptanceHelp: 'Turn this on when the change matters, such as new risks or conditions. Leave it off for typo fixes.',
        firstVersionNote: 'This is the first version: every customer will be asked to accept it.',
        submit: 'Upload',
        selected: 'Selected: {{name}} ({{size}})',
      },
      history: {
        title: 'Version history',
        version: 'Version {{version}}',
        reaccept: 'Customers asked to accept again',
        noReaccept: 'Earlier acceptances still valid',
        current: 'Current',
      },
      archive: {
        title: 'Archive consent form?',
        message: '"{{name}}" will no longer be asked for. Accepted versions stay stored as evidence.',
        confirm: 'Archive',
      },
      feedback: {
        created: 'Consent form created.',
        updated: 'Consent form updated.',
        uploaded: 'New version uploaded.',
        archived: 'Consent form archived.',
        restored: 'Consent form restored.',
      },
      validation: {
        nameRequired: 'Enter a name for each language.',
        validityInvalid: 'Enter a whole number of months between 1 and 120.',
        fileRequired: 'Choose a PDF file.',
        fileType: 'Only PDF files can be used.',
        fileSize: 'The PDF must be 10 MB or smaller.',
      },
    },
    serviceField: {
      label: 'Consent form',
      none: 'No consent form',
      help: 'Customers must read and accept this PDF before booking this service for the first time.',
      upgrade: 'Upgrade to Pro to ask customers to accept a consent form.',
      manageHint: 'Manage the PDFs in the Consent Forms tab.',
      noForms: 'No consent forms yet. Add one in the Consent Forms tab.',
      archived: '{{name}} (archived)',
    },
    derived: {
      title: 'Consent forms',
      help: 'Customers accept these before buying, because the included services ask for them.',
      none: 'None of the included services asks for a consent form.',
    },
    staff: {
      pending_one: '{{count}} consent form pending',
      pending_other: '{{count}} consent forms pending',
      accepted: 'Consent forms accepted',
      sectionTitle: 'Consent forms',
      acceptedOn: 'Accepted {{date}}',
      sendLink: 'Send consent link',
      sending: 'Sending...',
      sent: 'Consent link sent.',
      sentEmailAndSms: 'Consent link sent by email and SMS.',
      sentSms: 'Consent link sent by SMS.',
      notSent: 'The customer has no email or phone to send the link to.',
      nothingPending: 'Nothing pending: the customer has accepted every form.',
      bookingAlert_one: 'This customer has not accepted {{count}} consent form yet. You can book now and send them a link to accept it.',
      bookingAlert_other: 'This customer has not accepted {{count}} consent forms yet. You can book now and send them a link to accept them.',
      purchaseAlert_one: 'This customer has not accepted {{count}} consent form for the included services. Send them a link after the sale.',
      purchaseAlert_other: 'This customer has not accepted {{count}} consent forms for the included services. Send them a link after the sale.',
    },
    customerCard: {
      title: 'Consent forms',
      subtitle: 'Forms this customer read to the end and accepted.',
      empty: 'No consent forms accepted yet.',
      version: 'Version {{version}}',
      acceptedAt: 'Accepted {{date}}',
      appointment: 'Appointment on {{date}}',
      certificate: 'Certificate',
      context: {
        Booking: 'While booking',
        ServicePackagePurchase: 'When buying a package',
        MembershipPurchase: 'When joining a membership',
        PendingRequest: 'From a request',
      },
    },
    customerPage: {
      title: 'Consent forms',
      subtitle: 'The store asks you to read and accept these forms before your service.',
      empty: 'You have no consent forms to accept.',
      appointment: 'Appointment on {{date}} at {{time}}',
      package: 'Package: {{name}}',
      membership: 'Membership: {{name}}',
      review: 'Read and accept',
      accepted: 'Thank you. Your acceptance was saved.',
      loadError: 'Your consent forms could not be loaded.',
      pendingChip: 'Consent pending',
    },
  },
  customer: {
    navigation: {
      consentForms: 'Consent forms',
    },
  },
};

export const consentFormTranslationsPt = {
  servicesManagement: {
    tabs: {
      consentForms: 'Formulários de Consentimento',
    },
  },
  consentForms: {
    unnamed: 'Formulário de consentimento',
    viewer: {
      title: 'Formulário de consentimento',
      counter: '{{current}}/{{total}}',
      next: 'Seguinte',
      acceptOne: 'Aceitar Formulário de Consentimento',
      acceptMany: 'Aceitar Formulários de Consentimento',
      scrollHint: 'Percorra o documento até ao fim para continuar.',
      readContinue: 'Lido até ao fim. Continue para o formulário seguinte.',
      acceptStatement_one: 'Ao aceitar, confirma que leu e concorda com este formulário de consentimento.',
      acceptStatement_other: 'Ao aceitar, confirma que leu e concorda com os {{count}} formulários de consentimento.',
      download: 'Descarregar PDF',
      loadError: 'Não foi possível carregar o formulário de consentimento.',
      endOfDocument: 'Fim do documento',
    },
    errors: {
      accept: 'Não foi possível guardar a sua aceitação. Tente novamente.',
      load: 'Não foi possível verificar os formulários de consentimento. Tente novamente.',
      required: 'Leia e aceite os formulários de consentimento antes de continuar.',
      loading: 'Não foi possível carregar os formulários de consentimento.',
      create: 'Não foi possível criar o formulário de consentimento.',
      update: 'Não foi possível atualizar o formulário de consentimento.',
      upload: 'Não foi possível carregar o PDF.',
      archive: 'Não foi possível arquivar o formulário de consentimento.',
      restore: 'Não foi possível restaurar o formulário de consentimento.',
      preview: 'Não foi possível abrir o PDF.',
      sendRequest: 'Não foi possível enviar a ligação de consentimento.',
      certificate: 'Não foi possível descarregar o certificado.',
      acceptances: 'Não foi possível carregar os formulários aceites.',
      status: 'Não foi possível carregar o estado do consentimento.',
    },
    apiErrors: {
      ConsentFormNotFound: 'Este formulário de consentimento já não existe.',
      InvalidConsentFormData: 'Verifique os dados do formulário de consentimento e tente novamente.',
      ConsentFormArchived: 'Este formulário de consentimento está arquivado.',
      ConsentFormRequired: 'Leia e aceite os formulários de consentimento antes de continuar.',
      ConsentFormVersionNotCurrent: 'O formulário foi atualizado enquanto o lia. Feche esta janela e tente novamente para ler a versão mais recente.',
      ConsentFormHasNoVersion: 'Carregue primeiro um PDF para este formulário.',
      ConsentFormNotReadToEnd: 'Percorra todos os formulários até ao fim antes de aceitar.',
      InvalidFileType: 'Apenas ficheiros PDF podem ser usados como formulários de consentimento.',
      FileTooLarge: 'O PDF deve ter no máximo 10 MB.',
      PlanUpgradeRequired: 'Mude para o Pro para usar formulários de consentimento.',
    },
    management: {
      addNew: 'Adicionar Formulário',
      noForms: 'Ainda não há formulários de consentimento',
      emptyHint: 'Carregue um PDF que os clientes devem ler e aceitar antes da primeira visita e escolha-o em cada serviço.',
      intro: 'Os clientes têm de percorrer o PDF até ao fim e aceitá-lo antes de marcar um serviço que o use, ou antes de comprar um pacote ou assinatura que inclua esse serviço.',
      card: {
        currentVersion: 'Versão {{version}}',
        uploadedAt: 'Carregado a {{date}}',
        noFile: 'Ainda sem PDF',
        services_one: 'Usado em {{count}} serviço',
        services_other: 'Usado em {{count}} serviços',
        noServices: 'Ainda não é usado em nenhum serviço',
        validity: 'Válido durante {{count}} meses',
        noExpiry: 'Nunca expira',
        active: 'Ativo',
        archived: 'Arquivado',
        fileHash: 'SHA-256',
      },
      actions: {
        edit: 'Editar',
        upload: 'Carregar nova versão',
        uploadFirst: 'Carregar PDF',
        preview: 'Pré-visualizar',
        archive: 'Arquivar',
        restore: 'Restaurar',
        history: 'Histórico de versões',
      },
      dialog: {
        createTitle: 'Novo formulário de consentimento',
        editTitle: 'Editar formulário de consentimento',
        name: 'Nome',
        validityMonths: 'Validade (meses)',
        validityPlaceholder: 'Nunca expira',
        validityHelp: 'Deixe vazio se uma aceitação for suficiente. Indique um número de meses para pedir novamente de forma periódica.',
        file: 'Ficheiro PDF',
        save: 'Guardar',
        create: 'Criar',
      },
      upload: {
        title: 'Carregar nova versão',
        choose: 'Escolher PDF',
        replace: 'Escolher outro PDF',
        fileHelp: 'Apenas PDF, até 10 MB. As versões anteriores ficam guardadas como prova.',
        requiresReacceptance: 'Pedir aos clientes que aceitaram uma versão anterior que aceitem novamente',
        requiresReacceptanceHelp: 'Ative quando a alteração for relevante, como novos riscos ou condições. Deixe desativado para correções de texto.',
        firstVersionNote: 'Esta é a primeira versão: todos os clientes terão de a aceitar.',
        submit: 'Carregar',
        selected: 'Selecionado: {{name}} ({{size}})',
      },
      history: {
        title: 'Histórico de versões',
        version: 'Versão {{version}}',
        reaccept: 'Clientes chamados a aceitar novamente',
        noReaccept: 'Aceitações anteriores continuam válidas',
        current: 'Atual',
      },
      archive: {
        title: 'Arquivar formulário de consentimento?',
        message: '"{{name}}" deixará de ser pedido. As versões aceites continuam guardadas como prova.',
        confirm: 'Arquivar',
      },
      feedback: {
        created: 'Formulário de consentimento criado.',
        updated: 'Formulário de consentimento atualizado.',
        uploaded: 'Nova versão carregada.',
        archived: 'Formulário de consentimento arquivado.',
        restored: 'Formulário de consentimento restaurado.',
      },
      validation: {
        nameRequired: 'Indique um nome para cada idioma.',
        validityInvalid: 'Indique um número inteiro de meses entre 1 e 120.',
        fileRequired: 'Escolha um ficheiro PDF.',
        fileType: 'Apenas ficheiros PDF podem ser usados.',
        fileSize: 'O PDF deve ter no máximo 10 MB.',
      },
    },
    serviceField: {
      label: 'Formulário de consentimento',
      none: 'Sem formulário de consentimento',
      help: 'Os clientes têm de ler e aceitar este PDF antes de marcarem este serviço pela primeira vez.',
      upgrade: 'Mude para o Pro para pedir aos clientes que aceitem um formulário de consentimento.',
      manageHint: 'Gira os PDFs no separador Formulários de Consentimento.',
      noForms: 'Ainda não há formulários. Adicione um no separador Formulários de Consentimento.',
      archived: '{{name}} (arquivado)',
    },
    derived: {
      title: 'Formulários de consentimento',
      help: 'Os clientes aceitam estes formulários antes de comprar, porque os serviços incluídos os pedem.',
      none: 'Nenhum dos serviços incluídos pede um formulário de consentimento.',
    },
    staff: {
      pending_one: '{{count}} formulário de consentimento pendente',
      pending_other: '{{count}} formulários de consentimento pendentes',
      accepted: 'Formulários de consentimento aceites',
      sectionTitle: 'Formulários de consentimento',
      acceptedOn: 'Aceite a {{date}}',
      sendLink: 'Enviar ligação de consentimento',
      sending: 'A enviar...',
      sent: 'Ligação de consentimento enviada.',
      sentEmailAndSms: 'Ligação de consentimento enviada por email e SMS.',
      sentSms: 'Ligação de consentimento enviada por SMS.',
      notSent: 'O cliente não tem email nem telefone para receber a ligação.',
      nothingPending: 'Nada pendente: o cliente já aceitou todos os formulários.',
      bookingAlert_one: 'Este cliente ainda não aceitou {{count}} formulário de consentimento. Pode marcar agora e enviar-lhe uma ligação para o aceitar.',
      bookingAlert_other: 'Este cliente ainda não aceitou {{count}} formulários de consentimento. Pode marcar agora e enviar-lhe uma ligação para os aceitar.',
      purchaseAlert_one: 'Este cliente ainda não aceitou {{count}} formulário de consentimento dos serviços incluídos. Envie-lhe uma ligação depois da venda.',
      purchaseAlert_other: 'Este cliente ainda não aceitou {{count}} formulários de consentimento dos serviços incluídos. Envie-lhe uma ligação depois da venda.',
    },
    customerCard: {
      title: 'Formulários de consentimento',
      subtitle: 'Formulários que este cliente leu até ao fim e aceitou.',
      empty: 'Ainda não aceitou formulários de consentimento.',
      version: 'Versão {{version}}',
      acceptedAt: 'Aceite a {{date}}',
      appointment: 'Marcação de {{date}}',
      certificate: 'Certificado',
      context: {
        Booking: 'Ao marcar',
        ServicePackagePurchase: 'Ao comprar um pacote',
        MembershipPurchase: 'Ao aderir a uma assinatura',
        PendingRequest: 'A partir de um pedido',
      },
    },
    customerPage: {
      title: 'Formulários de consentimento',
      subtitle: 'A loja pede que leia e aceite estes formulários antes do seu serviço.',
      empty: 'Não tem formulários de consentimento por aceitar.',
      appointment: 'Marcação a {{date}} às {{time}}',
      package: 'Pacote: {{name}}',
      membership: 'Assinatura: {{name}}',
      review: 'Ler e aceitar',
      accepted: 'Obrigado. A sua aceitação foi guardada.',
      loadError: 'Não foi possível carregar os seus formulários de consentimento.',
      pendingChip: 'Consentimento pendente',
    },
  },
  customer: {
    navigation: {
      consentForms: 'Consentimentos',
    },
  },
};

export const consentFormTranslationsEs = {
  servicesManagement: {
    tabs: {
      consentForms: 'Formularios de Consentimiento',
    },
  },
  consentForms: {
    unnamed: 'Formulario de consentimiento',
    viewer: {
      title: 'Formulario de consentimiento',
      counter: '{{current}}/{{total}}',
      next: 'Siguiente',
      acceptOne: 'Aceptar Formulario de Consentimiento',
      acceptMany: 'Aceptar Formularios de Consentimiento',
      scrollHint: 'Desplázate hasta el final del documento para continuar.',
      readContinue: 'Leído hasta el final. Continúa con el siguiente formulario.',
      acceptStatement_one: 'Al aceptar, confirmas que has leído y aceptas este formulario de consentimiento.',
      acceptStatement_other: 'Al aceptar, confirmas que has leído y aceptas los {{count}} formularios de consentimiento.',
      download: 'Descargar PDF',
      loadError: 'No se pudo cargar el formulario de consentimiento.',
      endOfDocument: 'Fin del documento',
    },
    errors: {
      accept: 'No se pudo guardar tu aceptación. Inténtalo de nuevo.',
      load: 'No se pudieron comprobar los formularios de consentimiento. Inténtalo de nuevo.',
      required: 'Lee y acepta los formularios de consentimiento antes de continuar.',
      loading: 'No se pudieron cargar los formularios de consentimiento.',
      create: 'No se pudo crear el formulario de consentimiento.',
      update: 'No se pudo actualizar el formulario de consentimiento.',
      upload: 'No se pudo subir el PDF.',
      archive: 'No se pudo archivar el formulario de consentimiento.',
      restore: 'No se pudo restaurar el formulario de consentimiento.',
      preview: 'No se pudo abrir el PDF.',
      sendRequest: 'No se pudo enviar el enlace de consentimiento.',
      certificate: 'No se pudo descargar el certificado.',
      acceptances: 'No se pudieron cargar los formularios aceptados.',
      status: 'No se pudo cargar el estado del consentimiento.',
    },
    apiErrors: {
      ConsentFormNotFound: 'Este formulario de consentimiento ya no existe.',
      InvalidConsentFormData: 'Revisa los datos del formulario de consentimiento e inténtalo de nuevo.',
      ConsentFormArchived: 'Este formulario de consentimiento está archivado.',
      ConsentFormRequired: 'Lee y acepta los formularios de consentimiento antes de continuar.',
      ConsentFormVersionNotCurrent: 'El formulario se actualizó mientras lo leías. Cierra esta ventana e inténtalo de nuevo para leer la última versión.',
      ConsentFormHasNoVersion: 'Sube primero un PDF a este formulario.',
      ConsentFormNotReadToEnd: 'Desplázate hasta el final de cada formulario antes de aceptar.',
      InvalidFileType: 'Solo se pueden usar archivos PDF como formularios de consentimiento.',
      FileTooLarge: 'El PDF debe tener 10 MB como máximo.',
      PlanUpgradeRequired: 'Pásate a Pro para usar formularios de consentimiento.',
    },
    management: {
      addNew: 'Añadir Formulario',
      noForms: 'Todavía no hay formularios de consentimiento',
      emptyHint: 'Sube un PDF que tus clientes deben leer y aceptar antes de su primera visita y elígelo en cada servicio.',
      intro: 'Los clientes deben desplazarse hasta el final del PDF y aceptarlo antes de reservar un servicio que lo use, o antes de comprar un paquete o membresía que incluya ese servicio.',
      card: {
        currentVersion: 'Versión {{version}}',
        uploadedAt: 'Subido el {{date}}',
        noFile: 'Todavía sin PDF',
        services_one: 'Usado en {{count}} servicio',
        services_other: 'Usado en {{count}} servicios',
        noServices: 'Todavía no se usa en ningún servicio',
        validity: 'Válido durante {{count}} meses',
        noExpiry: 'Nunca caduca',
        active: 'Activo',
        archived: 'Archivado',
        fileHash: 'SHA-256',
      },
      actions: {
        edit: 'Editar',
        upload: 'Subir nueva versión',
        uploadFirst: 'Subir PDF',
        preview: 'Vista previa',
        archive: 'Archivar',
        restore: 'Restaurar',
        history: 'Historial de versiones',
      },
      dialog: {
        createTitle: 'Nuevo formulario de consentimiento',
        editTitle: 'Editar formulario de consentimiento',
        name: 'Nombre',
        validityMonths: 'Validez (meses)',
        validityPlaceholder: 'Nunca caduca',
        validityHelp: 'Déjalo vacío si basta con una aceptación. Indica un número de meses para pedirlo de nuevo periódicamente.',
        file: 'Archivo PDF',
        save: 'Guardar',
        create: 'Crear',
      },
      upload: {
        title: 'Subir nueva versión',
        choose: 'Elegir PDF',
        replace: 'Elegir otro PDF',
        fileHelp: 'Solo PDF, hasta 10 MB. Las versiones anteriores se guardan como prueba.',
        requiresReacceptance: 'Pedir a los clientes que aceptaron una versión anterior que acepten de nuevo',
        requiresReacceptanceHelp: 'Actívalo cuando el cambio sea importante, como nuevos riesgos o condiciones. Déjalo desactivado para correcciones de texto.',
        firstVersionNote: 'Es la primera versión: todos los clientes tendrán que aceptarla.',
        submit: 'Subir',
        selected: 'Seleccionado: {{name}} ({{size}})',
      },
      history: {
        title: 'Historial de versiones',
        version: 'Versión {{version}}',
        reaccept: 'Se pidió a los clientes aceptar de nuevo',
        noReaccept: 'Las aceptaciones anteriores siguen válidas',
        current: 'Actual',
      },
      archive: {
        title: '¿Archivar el formulario de consentimiento?',
        message: '"{{name}}" dejará de pedirse. Las versiones aceptadas se siguen guardando como prueba.',
        confirm: 'Archivar',
      },
      feedback: {
        created: 'Formulario de consentimiento creado.',
        updated: 'Formulario de consentimiento actualizado.',
        uploaded: 'Nueva versión subida.',
        archived: 'Formulario de consentimiento archivado.',
        restored: 'Formulario de consentimiento restaurado.',
      },
      validation: {
        nameRequired: 'Indica un nombre para cada idioma.',
        validityInvalid: 'Indica un número entero de meses entre 1 y 120.',
        fileRequired: 'Elige un archivo PDF.',
        fileType: 'Solo se pueden usar archivos PDF.',
        fileSize: 'El PDF debe tener 10 MB como máximo.',
      },
    },
    serviceField: {
      label: 'Formulario de consentimiento',
      none: 'Sin formulario de consentimiento',
      help: 'Los clientes deben leer y aceptar este PDF antes de reservar este servicio por primera vez.',
      upgrade: 'Pásate a Pro para pedir a los clientes que acepten un formulario de consentimiento.',
      manageHint: 'Gestiona los PDF en la pestaña Formularios de Consentimiento.',
      noForms: 'Todavía no hay formularios. Añade uno en la pestaña Formularios de Consentimiento.',
      archived: '{{name}} (archivado)',
    },
    derived: {
      title: 'Formularios de consentimiento',
      help: 'Los clientes los aceptan antes de comprar, porque los servicios incluidos los piden.',
      none: 'Ninguno de los servicios incluidos pide un formulario de consentimiento.',
    },
    staff: {
      pending_one: '{{count}} formulario de consentimiento pendiente',
      pending_other: '{{count}} formularios de consentimiento pendientes',
      accepted: 'Formularios de consentimiento aceptados',
      sectionTitle: 'Formularios de consentimiento',
      acceptedOn: 'Aceptado el {{date}}',
      sendLink: 'Enviar enlace de consentimiento',
      sending: 'Enviando...',
      sent: 'Enlace de consentimiento enviado.',
      sentEmailAndSms: 'Enlace de consentimiento enviado por email y SMS.',
      sentSms: 'Enlace de consentimiento enviado por SMS.',
      notSent: 'El cliente no tiene email ni teléfono para recibir el enlace.',
      nothingPending: 'Nada pendiente: el cliente ya aceptó todos los formularios.',
      bookingAlert_one: 'Este cliente todavía no aceptó {{count}} formulario de consentimiento. Puedes reservar ahora y enviarle un enlace para aceptarlo.',
      bookingAlert_other: 'Este cliente todavía no aceptó {{count}} formularios de consentimiento. Puedes reservar ahora y enviarle un enlace para aceptarlos.',
      purchaseAlert_one: 'Este cliente todavía no aceptó {{count}} formulario de consentimiento de los servicios incluidos. Envíale un enlace después de la venta.',
      purchaseAlert_other: 'Este cliente todavía no aceptó {{count}} formularios de consentimiento de los servicios incluidos. Envíale un enlace después de la venta.',
    },
    customerCard: {
      title: 'Formularios de consentimiento',
      subtitle: 'Formularios que este cliente leyó hasta el final y aceptó.',
      empty: 'Todavía no aceptó formularios de consentimiento.',
      version: 'Versión {{version}}',
      acceptedAt: 'Aceptado el {{date}}',
      appointment: 'Reserva del {{date}}',
      certificate: 'Certificado',
      context: {
        Booking: 'Al reservar',
        ServicePackagePurchase: 'Al comprar un paquete',
        MembershipPurchase: 'Al unirse a una membresía',
        PendingRequest: 'Desde una solicitud',
      },
    },
    customerPage: {
      title: 'Formularios de consentimiento',
      subtitle: 'La tienda te pide leer y aceptar estos formularios antes de tu servicio.',
      empty: 'No tienes formularios de consentimiento por aceptar.',
      appointment: 'Reserva el {{date}} a las {{time}}',
      package: 'Paquete: {{name}}',
      membership: 'Membresía: {{name}}',
      review: 'Leer y aceptar',
      accepted: 'Gracias. Tu aceptación se ha guardado.',
      loadError: 'No se pudieron cargar tus formularios de consentimiento.',
      pendingChip: 'Consentimiento pendiente',
    },
  },
  customer: {
    navigation: {
      consentForms: 'Consentimientos',
    },
  },
};
